/**
 * Splash screen dedicada exibida no boot do app (uma vez por sessão).
 * Usa a mesma logomarca xxl com animação de entrada (fade + escala + pulso
 * neon) e sai suavemente antes de revelar o conteúdo.
 *
 * Controle:
 * - sessionStorage flag `zappy:splash:seen` evita reexibir em navegações internas
 *   ou reloads dentro da mesma sessão.
 * - Respeita `prefers-reduced-motion` (fallback estático).
 */
import { useEffect, useState } from "react";
import PulsoMark from "@/components/PulsoMark";

const SESSION_KEY = "zappy:splash:seen";
const SPLASH_DURATION_MS = 1600;
const EXIT_MS = 450;

// Mesma animação de antes, feita só com CSS (sem a biblioteca framer-motion),
// para não pesar no primeiro carregamento do app.
const SPLASH_CSS = `
@keyframes zsplash-halo { from { opacity: 0; transform: scale(.6) } to { opacity: 1; transform: scale(1.1) } }
@keyframes zsplash-ring { 0% { opacity: 0; transform: scale(.85) } 50% { opacity: .9; transform: scale(1.05) } 100% { opacity: .6; transform: scale(1) } }
@keyframes zsplash-mark { from { opacity: 0; transform: translateY(8px) scale(.7) } to { opacity: 1; transform: none } }
@keyframes zsplash-fadein { from { opacity: 0 } to { opacity: 1 } }
@keyframes zsplash-bar { from { width: 0% } to { width: 100% } }
.zsplash { transition: opacity ${EXIT_MS}ms ease-out; }
.zsplash.is-leaving { opacity: 0; }
.zsplash-halo { animation: zsplash-halo 1.2s ease-out both; }
.zsplash-ring { animation: zsplash-ring 1.4s ease-out both; }
.zsplash-mark { animation: zsplash-mark .9s cubic-bezier(.22,1,.36,1) both; }
.zsplash-track { animation: zsplash-fadein .3s .4s both; }
.zsplash-bar { animation: zsplash-bar ${(SPLASH_DURATION_MS - 200) / 1000}s ease-in-out both; }
@media (prefers-reduced-motion: reduce) {
  .zsplash-halo, .zsplash-ring, .zsplash-mark, .zsplash-track, .zsplash-bar { animation-duration: 1ms; animation-delay: 0ms; }
}
`;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export default function SplashScreen() {
  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return sessionStorage.getItem(SESSION_KEY) !== "1";
    } catch {
      return true;
    }
  });
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (!visible) return;
    const reduce = prefersReducedMotion();
    const duration = reduce ? 700 : SPLASH_DURATION_MS;
    let exitTimer: number | undefined;
    const timer = window.setTimeout(() => {
      try {
        sessionStorage.setItem(SESSION_KEY, "1");
      } catch {
        /* ignore */
      }
      // Sai suavemente (fade de 0,45 s) antes de desmontar.
      setLeaving(true);
      exitTimer = window.setTimeout(() => setVisible(false), EXIT_MS);
    }, duration);
    return () => {
      window.clearTimeout(timer);
      if (exitTimer) window.clearTimeout(exitTimer);
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Carregando Zappy"
      className={`zsplash fixed inset-0 z-[9999] flex items-center justify-center bg-background overflow-hidden${leaving ? " is-leaving" : ""}`}
    >
      <style>{SPLASH_CSS}</style>
      {/* Halo radial sutil por trás da logo */}
      <div
        aria-hidden
        className="zsplash-halo absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 50% 50%, oklch(0.86 0.19 125 / 0.22), transparent 60%)",
        }}
      />

      {/* Anel neon pulsante */}
      <div
        aria-hidden
        className="zsplash-ring absolute rounded-full"
        style={{
          width: "min(70vw, 22rem)",
          height: "min(70vw, 22rem)",
          boxShadow:
            "0 0 60px oklch(0.86 0.19 125 / 0.30), inset 0 0 40px oklch(0.86 0.19 125 / 0.22)",
          border: "1px solid oklch(0.86 0.19 125 / 0.35)",
        }}
      />

      {/* Marca PULSO — radar vivo: anéis de sonar + varredura girando + núcleo pulsante */}
      <div className="zsplash-mark relative z-10 flex flex-col items-center gap-3">
        <PulsoMark size={168} live />
        <span
          style={{
            fontFamily: "'Space Grotesk', 'Montserrat', sans-serif",
            fontWeight: 800,
            fontSize: 42,
            letterSpacing: "-.04em",
            color: "hsl(var(--foreground))",
          }}
        >
          zappy
        </span>
      </div>

      {/* Barra de progresso fina no rodapé */}
      <div
        aria-hidden
        className="zsplash-track absolute bottom-[12vh] h-[3px] rounded-full overflow-hidden"
        style={{ width: "min(50vw, 14rem)", background: "hsl(var(--muted) / 0.4)" }}
      >
        <div
          className="zsplash-bar h-full"
          style={{
            background:
              "linear-gradient(90deg, oklch(0.86 0.19 125), oklch(0.70 0.13 165), oklch(0.72 0.12 240))",
          }}
        />
      </div>
    </div>
  );
}
